/**
 * Loads Google Maps script once for the whole app.
 * Use useGoogleMaps() in DashboardMap and GoogleMapPicker to avoid loading the script twice
 * (which causes "This page can't load Google Maps correctly" and white screen in dialogs).
 * For production: set REACT_APP_GOOGLE_MAPS_API_KEY in .env to a key with Maps JavaScript API
 * enabled and (if required) billing; otherwise the map may show "For development purposes only".
 *
 * IMPORTANT: do NOT change useJsApiLoader `id` / `language` on i18n language switch —
 * that remounts the Maps script and white-screens the app. Locale for map chrome is
 * taken once from the initial UI language; custom map legend still follows i18n.
 */
import React, { createContext, useContext, useMemo, useRef } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { resolveAppLanguage } from '../localization/i18n';

const FALLBACK_GOOGLE_MAPS_API_KEY = 'AIzaSyD0qFnUtN1n-8OacrEekGeLwF2diYJi-tU';

const GoogleMapsContext = createContext({
  isLoaded: false,
  loadError: null,
  apiKey: '',
  language: 'en',
});

function initialMapsLanguage() {
  try {
    const stored = localStorage.getItem('Lan');
    if (stored === 'RU') return 'ru';
    if (stored === 'EN') return 'en';
  } catch (_) {
    /* ignore */
  }
  return resolveAppLanguage() === 'ru' ? 'ru' : 'en';
}

export function GoogleMapsProvider({ children }) {
  // Freeze loader options for the lifetime of the provider (page load).
  const languageRef = useRef(null);
  if (languageRef.current == null) {
    languageRef.current = initialMapsLanguage();
  }
  const language = languageRef.current;

  const envKey = typeof process !== 'undefined' ? process.env.REACT_APP_GOOGLE_MAPS_API_KEY : '';
  const apiKey = envKey && String(envKey).trim() ? envKey : FALLBACK_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: apiKey,
    language,
    region: language === 'ru' ? 'RU' : 'US',
  });

  const value = useMemo(
    () => ({ isLoaded, loadError, apiKey, language }),
    [isLoaded, loadError, apiKey, language],
  );

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
}

export function useGoogleMaps() {
  return useContext(GoogleMapsContext);
}
